<template>
  <div ref="wrapper">
    <HorizontalScroll
      v-if="loading"
      placeholder
      :full-bleed="true"
      class="pt-lg">
      <ImageTileButtonPlaceholder
        v-for="i in 5"
        :key="`placeholder-${i}`"
        :class="tileClasses"/>
    </HorizontalScroll>
    <div
      v-else-if="shouldShow"
      class="d-flex flex-column row-gap-lg pt-lg">
      <HorizontalScroll
        :full-bleed="true"
        title="Featured">
        <FeaturedSectionTileButton
          v-for="section in primarySections"
          :key="section.id"
          :section-id="section.id"
          :class="tileClasses"/>
      </HorizontalScroll>
      <MyClosetSectionPreview
        v-if="previewSectionId"
        :section-id="previewSectionId"/>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, useTemplateRef } from 'vue'
import FeaturedSectionTileButton from '@/components/closet/FeaturedSectionTileButton.vue'
import ImageTileButtonPlaceholder from '@/components/global/ImageTileButtonPlaceholder.vue'
import HorizontalScroll from '@shared/components/ADORN/HorizontalScroll.vue'
import { useElementVisibility, whenever } from '@vueuse/core'
import useScreenSize from '@shared/composables/screenSize.js'
import { useClosetStore } from '@shared/stores/closet.js'
import { storeToRefs } from 'pinia'
import MyClosetSectionPreview from '@/components/closet/MyClosetSectionPreview.vue'

const closetStore = useClosetStore()
const { featuredSectionsLoaded, featuredSections } = storeToRefs(closetStore)
const visibleSections = computed(() => featuredSections.value.filter(section => section.length === null || section.length > 6))

const previewSectionId = computed(() => visibleSections.value.find(section => section.slot === 0)?.id)
const primarySections = computed(() => visibleSections.value.filter(section => section.id !== previewSectionId.value))

const shouldShow = computed(() => featuredSectionsLoaded.value && visibleSections.value.length > 0)
const tileClasses = 'featured-section col-12 col-sm-6 col-md-6 col-lg-3'

const loading = ref(!featuredSections.value?.[0]?.length)
const wrapper = useTemplateRef('wrapper')
const visible = useElementVisibility(wrapper)

const { isMobile } = useScreenSize()

const loadSectionsInBatches = async () => {
  const batchSize = isMobile.value ? 2 : 4
  const sections = featuredSections.value.filter(section => section.length === null)
  for (let i = 0; i < sections.length; i += batchSize) {
    const batch = sections.slice(i, i + batchSize)
    const promises = batch.filter(section => section.length === null).map(section =>
      closetStore.loadStyleColors({
        id: section.id,
        amount: section.slot === 0 ? 20 : 6
      })
    )
    await Promise.allSettled(promises)
    loading.value = false
  }
}

whenever(
  () => visible.value && loading.value && featuredSectionsLoaded.value,
  loadSectionsInBatches,
  {
    immediate: true,
    once: true
  }
)

defineOptions({
  compatConfig: {
    // The useElementVisibility composable is based on useIntersectionObserver
    // which doesn't use the deep option on the array it watches. The composable
    // works so we're suppressing the warning.
    WATCH_ARRAY: false
  }
})
</script>

<style lang="scss" scoped>
$thumbnailWidth: 300px;
$thumbnailHeight: 250px;

.banner-image {
  width: 100%;
  height: 100%;
}

.image-container {
  position: relative;
  height: 207px;
}

.custom-image {
  width: 334px;
  height: 207px;
  border-radius: 2px;
  object-fit: cover;
}

.featured-section {
  display: flex;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.details {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 74px;
  justify-content: center;
  align-items: center;
  background-color: rgba($armor, 0.75);
  text-align: center;
  padding: 18px;
  box-sizing: border-box;
}

.placeholders {
  display: flex;
  gap: 36px;
  overflow: hidden;
}

.section-placeholder {
  height: $thumbnailHeight;
  width: $thumbnailWidth;
  flex-shrink: 0;
  @include placeholder-loading-animation;
}
</style>
